import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Search, Menu, X, Check, Globe } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const navKeys = [
  { key: 'nav.women', to: '/shop?category=Women' },
  { key: 'nav.handbags', to: '/shop?category=Handbags' },
  { key: 'nav.watches', to: '/shop?category=Watches' },
  { key: 'nav.jewelry', to: '/shop?category=Jewelry' },
  { key: 'nav.shoes', to: '/shop?category=Shoes' },
  { key: 'nav.designers', to: '/designers' },
  { key: 'nav.editorial', to: '/editorial' },
];

export default function Header() {
  const { t, lang, setLang, languages, cart, user } = useApp();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const cartCount = cart.reduce((a, i) => a + i.quantity, 0);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/shop?q=${encodeURIComponent(q.trim())}`); setSearchOpen(false); }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-border/60" data-testid="site-header">
      {/* Announcement bar */}
      <div className="bg-foreground text-background overline text-center py-2" data-testid="announcement-bar">
        Complimentary Authentication · Free Shipping on Orders Over $500
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden p-2 -ml-2" data-testid="mobile-menu-btn"><Menu size={20} /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80">
              <nav className="mt-12 flex flex-col gap-6">
                {navKeys.map((n) => (
                  <Link key={n.key} to={n.to} onClick={() => setOpen(false)} className="font-display text-2xl" data-testid={`mob-${n.key}`}>{t(n.key)}</Link>
                ))}
                <Link to="/sell" onClick={() => setOpen(false)} className="font-display text-2xl text-accent" data-testid="mob-sell">{t('nav.sell')}</Link>
              </nav>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/" className="font-display text-xl md:text-2xl tracking-[0.25em] flex-1 md:flex-none text-center md:text-left" data-testid="logo-link">
            JJBSTYLISH
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 flex-1 justify-center" data-testid="primary-nav">
            {navKeys.slice(0, 6).map((n) => (
              <NavLink key={n.key} to={n.to} className="overline link-underline" data-testid={`nav-${n.key}`}>
                {t(n.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 md:gap-3">
            <button onClick={() => setSearchOpen((v) => !v)} className="p-2" aria-label="search" data-testid="header-search-btn"><Search size={18} strokeWidth={1.25} /></button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 flex items-center gap-1 overline" data-testid="lang-trigger">
                  <Globe size={16} strokeWidth={1.25} />
                  <span className="hidden md:inline">{languages.find((l) => l.code === lang)?.short || 'EN'}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {languages.map((l) => (
                  <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className="flex justify-between" data-testid={`lang-${l.code}`}>
                    <span>{l.label}</span>
                    {lang === l.code && <Check size={14} />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to={user ? '/account' : '/login'} className="p-2" data-testid="account-btn">
              <User size={18} strokeWidth={1.25} />
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="overline text-accent px-2 py-1 border border-accent" data-testid="admin-link">Admin</Link>
            )}
            <Link to="/cart" className="p-2 relative" data-testid="cart-btn">
              <ShoppingBag size={18} strokeWidth={1.25} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-foreground text-background text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">{cartCount}</span>
              )}
            </Link>
          </div>
        </div>

        {/* Sub row: Sell / search */}
        <div className="hidden md:flex items-center justify-between border-t border-border/60 py-3">
          <Link to="/sell" className="overline text-accent link-underline" data-testid="sell-link">{t('nav.sell')} →</Link>
          <Link to="/about" className="overline link-underline" data-testid="about-link">{t('nav.about')}</Link>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="py-4 border-t border-border/60" data-testid="search-form">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('cta.search') + '…'}
              className="w-full bg-transparent border-b border-foreground py-3 text-lg font-display outline-none"
              data-testid="search-input"
            />
          </form>
        )}
      </div>
    </header>
  );
}
